import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  DEFAULT_IMAGE_POSITION,
  ImagePositionState,
  DESTINATION_ASPECT_RATIO,
  EXPERIENCE_ASPECT_RATIO,
} from './image-position-state';

@Component({
  selector: 'app-image-positioner',
  templateUrl: './image-positioner.component.html',
  styleUrls: ['./image-positioner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImagePositionerComponent implements OnDestroy {
  @Input() imageSrc: string = '';
  @Input() state: ImagePositionState | null = null;

  /**
   * CSS aspect-ratio string that MUST match the final preview/PDF container ratio.
   * This ensures object-fit:cover crops identically in both selector and preview,
   * so the object-position percentages produce the same focal point.
   *
   * Default: `'1517 / 769'` (destination hero ratio matching preview-itinerary).
   * Use `'16 / 9'` for experience/activity images.
   */
  @Input() aspectRatio: string = DESTINATION_ASPECT_RATIO;

  @Output() stateChange = new EventEmitter<ImagePositionState>();

  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  private isDragging = false;

  private readonly onMouseMove = this.handleMouseMove.bind(this);
  private readonly onMouseUp   = this.handleMouseUp.bind(this);
  private readonly onTouchMove = this.handleTouchMove.bind(this);
  private readonly onTouchEnd  = this.handleTouchEnd.bind(this);

  constructor(private cdr: ChangeDetectorRef) {}

  get currentX(): number {
    return this.state?.x ?? DEFAULT_IMAGE_POSITION.x;
  }

  get currentY(): number {
    return this.state?.y ?? DEFAULT_IMAGE_POSITION.y;
  }

  get objectPosition(): string {
    return `${this.currentX}% ${this.currentY}%`;
  }

  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    this.isDragging = true;
    this.updatePositionFromEvent(event.clientX, event.clientY);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup',   this.onMouseUp);
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.updatePositionFromEvent(event.clientX, event.clientY);
  }

  private handleMouseUp(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.removeListeners();
    this.emit();
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) return;
    event.preventDefault();
    this.isDragging = true;
    this.updatePositionFromEvent(event.touches[0].clientX, event.touches[0].clientY);
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    document.addEventListener('touchend',  this.onTouchEnd);
  }

  private handleTouchMove(event: TouchEvent): void {
    if (!this.isDragging || event.touches.length !== 1) return;
    event.preventDefault();
    this.updatePositionFromEvent(event.touches[0].clientX, event.touches[0].clientY);
  }

  private handleTouchEnd(): void {
    this.isDragging = false;
    this.removeListeners();
    this.emit();
  }

  private updatePositionFromEvent(clientX: number, clientY: number): void {
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    const newX = Math.min(100, Math.max(0, x));
    const newY = Math.min(100, Math.max(0, y));

    this.state = { x: newX, y: newY };
    this.cdr.markForCheck();
  }

  private emit(): void {
    this.stateChange.emit(this.state ?? DEFAULT_IMAGE_POSITION);
  }

  private removeListeners(): void {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup',   this.onMouseUp);
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('touchend',  this.onTouchEnd);
  }

  ngOnDestroy(): void {
    this.removeListeners();
  }
}
